import jspdf, { Font, jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { generateUuidV4 } from '../../../util/uuid';
import { MetadataError } from '@ep/error-handling';
import readiness_gauge_background from '../../../assets/readiness_gauge_front_page_temp/gauge_background_rev_new.svg';
import needle_image from '../../../assets/readiness_gauge_front_page_temp/needle.webp';
import { FUNCTION_UNKNOWN_FAILURE } from '../../../util/global_variables';

const SOURCE = 'readiness_pdf_conference';

// Type definitions
export interface GaugeScores {
  environmental: number;
  social: number;
  governance: number;
  overall: number;
}

// Constants
const A4_WIDTH = 595;
const A4_HEIGHT = 842;

const LAYOUT = {
  left_column: {
    center_x: 165,
    company_name_y: 200,
    company_name_max_width: 263,
    company_name_line_height: 38,
    overall_score_y: 495,
    progress_bars: {
      x: 55,
      start_y: 567,
      spacing: 57,
      width: 232,
      height: 17
    }
  },
  right_column: {
    x: 372,
    content_width: 205,
    heading_y: 200,
    content_start_y: 225,
    paragraph_spacing: 5 // Space between paragraphs
  },
  gauge: {
    center_x: 162,
    center_y: 365,
    needle_height: 200,
    needle_rotation_point_from_bottom: 181
  }
};

type RGB = [number, number, number];

const COLORS: Record<string, RGB> = {
  white: [255, 255, 255],
  teal: [78, 186, 167],
  dark_blue: [0, 50, 70]
};

const RESPONSE_TEXT = {
  policy: {
    truthy:
      'Your ESG policies are being well managed. Policies often evolve as organisations mature. With Syntiro, you can easily update, track and align your ESG policies to recognised frameworks, ensuring they stay current, compliant and approved by the right people.',
    falsy:
      'There may be some gaps in your ESG policy management. With Syntiro, you can easily track, update and align your ESG policies to recognised frameworks, ensuring they stay current, compliant and approved by the right people.'
  },
  environmental: {
    truthy:
      'Your utilities are being reported well. Energy and water use directly influence your operating costs and carbon footprint. With Syntiro, you can streamline reporting using either metered or static data. The Utility and Asset Management modules make it easy to monitor performance, uncover cost-saving opportunities and focus on actions that deliver real payback.',
    falsy:
      "It seems like it's time to focus on your utility management. Energy and water use directly influence your operating costs and carbon footprint. With Syntiro, you can report usage by source and renewable energy impact using either metered or static data. The Utility and Asset Management modules make it easy to monitor performance, uncover cost-saving opportunities and focus on actions that deliver real payback."
  },
  score: {
    beginner:
      "You're at the very beginning of your ESG journey. Let's explore how Syntiro can help you take the first step and turn your goals into action.",
    advanced:
      "You've built a strong ESG foundation, now it's time to take it further. Syntiro helps you streamline, automate and scale your reporting for even greater impact."
  }
};

const TEXT_CONTENT = {
  right_column_heading: 'What this means'
};

enum SCORE_THRESHOLDS {
  MIN = 0,
  MAX = 100
}

enum PILLARS {
  GOVERNANCE = 'Governance',
  SOCIAL = 'Social',
  ENVIRONMENTAL = 'Environmental'
}

// Helper functions for ESG response generation
const getStrongestPillar = (governance: number, social: number, environmental: number): string => {
  const scores = [
    { name: PILLARS.ENVIRONMENTAL, value: environmental },
    { name: PILLARS.SOCIAL, value: social },
    { name: PILLARS.GOVERNANCE, value: governance }
  ];

  return scores.reduce((max, current) => (current.value > max.value ? current : max)).name;
};

const getScoreParagraph = (
  score: number,
  governance: number,
  social: number,
  environmental: number
): string => {
  if (score === SCORE_THRESHOLDS.MIN) {
    return RESPONSE_TEXT.score.beginner;
  } else if (score === SCORE_THRESHOLDS.MAX) {
    return RESPONSE_TEXT.score.advanced;
  } else {
    return `Your strongest ESG pillar is ${getStrongestPillar(governance, social, environmental)}.`;
  }
};

const getPolicyParagraph = (has_policy: boolean): string => {
  return has_policy ? RESPONSE_TEXT.policy.truthy : RESPONSE_TEXT.policy.falsy;
};

const getEnvironmentalParagraph = (has_environmental_initiative: boolean): string => {
  return has_environmental_initiative
    ? RESPONSE_TEXT.environmental.truthy
    : RESPONSE_TEXT.environmental.falsy;
};

const generateESGResponseParagraphs = (
  score: number,
  governance: number,
  social: number,
  environmental: number,
  has_policy: boolean,
  has_environmental_initiative: boolean
): Array<string> => {
  return [
    getScoreParagraph(score, governance, social, environmental),
    getPolicyParagraph(has_policy),
    getEnvironmentalParagraph(has_environmental_initiative)
  ];
};

// Progress bar rendering
const drawProgressBar = (
  pdf: jsPDF,
  label: string,
  percentage: number,
  x: number,
  y: number,
  bar_width = LAYOUT.left_column.progress_bars.width,
  bar_height = LAYOUT.left_column.progress_bars.height
): void => {
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(11);
  pdf.setTextColor(...COLORS.white);
  pdf.text(label, x, y - 5);

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.text(`${percentage}%`, x + bar_width - 5, y - 5, { align: 'right' });

  const filled_width: number = (bar_width * percentage) / 100;
  pdf.setFillColor(...COLORS.teal);
  if (filled_width > 0) {
    pdf.roundedRect(x, y, filled_width, bar_height, 8, 8, 'F');
  }
};

// Needle rotation and rendering
const rotateNeedleOnCanvas = async (score: number): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img: HTMLImageElement = new Image();

    img.onload = () => {
      const canvas: HTMLCanvasElement = document.createElement('canvas');
      const canvas_size: number = Math.max(img.width, img.height) * 2;
      canvas.width = canvas_size;
      canvas.height = canvas_size;

      const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Score of 0% maps to -90°, 50% to 0°, 100% to +90°
      const angle_in_degrees: number = (score / 100) * 180 - 90;
      const angle_in_radians: number = (angle_in_degrees * Math.PI) / 180;

      ctx.save();
      ctx.translate(canvas_size / 2, canvas_size / 2);
      ctx.rotate(angle_in_radians);

      // Rotation point is 181px from the bottom of the needle image
      const rotation_point_from_bottom: number = LAYOUT.gauge.needle_rotation_point_from_bottom;
      ctx.drawImage(
        img,
        -img.width / 2,
        -(img.height - rotation_point_from_bottom),
        img.width,
        img.height
      );

      ctx.restore();

      // Converts dark pixels (near-black) to transparent for better compositing
      const image_data: ImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data: Uint8ClampedArray = image_data.data;

      for (let i = 0; i < data.length; i += 4) {
        const r: number = data[i];
        const g: number = data[i + 1];
        const b: number = data[i + 2];

        if (r < 30 && g < 30 && b < 30) {
          data[i + 3] = 0;
        }
      }

      ctx.putImageData(image_data, 0, 0);
      resolve(canvas.toDataURL('image/png', 1.0));
    };

    img.onerror = () => reject(new Error('Failed to load needle image'));
    img.src = needle_image;
  });
};

const addGaugeNeedle = async (
  pdf: jsPDF,
  score: number,
  center_x: number,
  center_y: number,
  needle_height: number
): Promise<void> => {
  const needle_data_url: string = await rotateNeedleOnCanvas(score);

  const img: HTMLImageElement = new Image();
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
    img.src = needle_data_url;
  });

  // Canvas is square, so extra space is needed to accommodate rotation
  const display_size: number = needle_height * 1.5;

  // Centers the rotated needle at the specified coordinates
  pdf.addImage(
    needle_data_url,
    'PNG',
    center_x - display_size / 2,
    center_y - display_size / 2,
    display_size,
    display_size,
    undefined,
    'FAST'
  );
};

// Background SVG rendering.
const renderSvgBackground = async (pdf: jsPDF): Promise<void> => {
  // Temporary off-screen container for SVG rendering.
  const container: HTMLDivElement = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-99999px';
  container.style.top = '0';
  container.style.width = `${A4_WIDTH}px`;
  container.style.height = `${A4_HEIGHT}px`;
  container.style.overflow = 'hidden';
  document.body.appendChild(container);

  try {
    const response: Response = await fetch(readiness_gauge_background);
    const svg_text: string = await response.text();
    container.innerHTML = svg_text;

    const svg_element: SVGSVGElement | null = container.querySelector('svg');
    if (!svg_element) {
      throw new Error('SVG element not found in background file');
    }

    svg_element.setAttribute('width', `${A4_WIDTH}`);
    svg_element.setAttribute('height', `${A4_HEIGHT}`);
    svg_element.style.display = 'block';

    // Ensures fonts and resources are fully loaded before rendering
    if (document.fonts) {
      await document.fonts.ready;
    }

    await new Promise((resolve) => setTimeout(resolve, 150));

    const canvas: HTMLCanvasElement = await html2canvas(container as HTMLElement, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      backgroundColor: null,
      logging: false,
      width: A4_WIDTH,
      height: A4_HEIGHT,
      windowWidth: A4_WIDTH,
      windowHeight: A4_HEIGHT
    });

    const img_data: string = canvas.toDataURL('image/png', 1.0);
    pdf.addImage(img_data, 'PNG', 0, 0, A4_WIDTH, A4_HEIGHT, undefined, 'FAST');
  } finally {
    document.body.removeChild(container);
  }
};

// Company name rendering with truncation logic
const renderCompanyName = (pdf: jsPDF, company_name: string): void => {
  const { center_x, company_name_y, company_name_max_width, company_name_line_height } =
    LAYOUT.left_column;

  // First check with the default size to see how many lines we need
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(32);
  pdf.setTextColor(...COLORS.white);

  const lines = pdf.splitTextToSize(company_name, company_name_max_width);

  if (lines.length === 1) {
    // If it fits on one line, use a larger font size
    pdf.setFontSize(42); // Increase from 32 to 48 (or adjust to your preference)
    pdf.text(company_name, center_x, company_name_y + 10, { align: 'center' });
  } else if (lines.length === 2) {
    // Keep original size for two lines
    pdf.text(lines[0], center_x, company_name_y, { align: 'center' });
    pdf.text(lines[1], center_x, company_name_y + company_name_line_height, { align: 'center' });
  } else {
    // Displays first line and truncates second line with ellipsis
    pdf.text(lines[0], center_x, company_name_y, { align: 'center' });

    const second_line = lines[1];
    const ellipsis = '...';
    let truncated_second_line = second_line;
    const test_width: number = pdf.getTextWidth(second_line + ellipsis);

    if (test_width > company_name_max_width) {
      const chars = truncated_second_line.split('');
      truncated_second_line = '';

      for (let i = 0; i < chars.length; i++) {
        const test_line = truncated_second_line + chars[i] + ellipsis;
        if (pdf.getTextWidth(test_line) > company_name_max_width) {
          break;
        }
        truncated_second_line += chars[i];
      }
      truncated_second_line = truncated_second_line.trim();
    }

    pdf.text(
      truncated_second_line + ellipsis,
      center_x,
      company_name_y + company_name_line_height,
      {
        align: 'center'
      }
    );
  }
};

// Score bars rendering
const renderScoreBars = (pdf: jsPDF, scores: GaugeScores): void => {
  const { x, start_y, spacing } = LAYOUT.left_column.progress_bars;

  drawProgressBar(pdf, '', scores.environmental, x, start_y);
  drawProgressBar(pdf, '', scores.social, x, start_y + spacing);
  drawProgressBar(pdf, '', scores.governance, x, start_y + spacing * 2);
};

// Overall score rendering
const renderOverallScore = (pdf: jsPDF, score: number): void => {
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(64);
  pdf.setTextColor(...COLORS.white);
  pdf.text(`${score}%`, LAYOUT.left_column.center_x, LAYOUT.left_column.overall_score_y, {
    align: 'center'
  });
};

// Right column content rendering
const renderRightColumnContent = (
  pdf: jsPDF,
  scores: GaugeScores,
  has_policy: boolean,
  has_environmental_initiative: boolean
): void => {
  const { x, content_width, heading_y, content_start_y, paragraph_spacing } = LAYOUT.right_column;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(24);
  pdf.setTextColor(...COLORS.white);
  pdf.text(TEXT_CONTENT.right_column_heading, x, heading_y);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(...COLORS.white);
  const line_height: number = pdf.getLineHeight() / pdf.internal.scaleFactor;
  pdf.setLineHeightFactor(1.4);

  // Generate dynamic paragraphs based on scores and flags
  const paragraphs: Array<string> = generateESGResponseParagraphs(
    scores.overall,
    scores.governance,
    scores.social,
    scores.environmental,
    has_policy,
    has_environmental_initiative
  );

  let current_y = content_start_y;

  // Render each paragraph dynamically
  paragraphs.forEach((paragraph) => {
    const lines = pdf.splitTextToSize(paragraph, content_width);
    pdf.text(lines, x, current_y);

    // Calculate the height of the current paragraph
    const paragraph_height: number = lines.length * line_height * 1.4; // 1.4 is the line height factor

    // Move Y position down for next paragraph
    current_y += paragraph_height + paragraph_spacing;
  });
};

// Text overlay composition
const addTextContent = (
  pdf: jsPDF,
  company_name: string,
  scores: GaugeScores,
  has_policy: boolean,
  has_environmental_initiative: boolean
): void => {
  renderCompanyName(pdf, company_name);
  renderOverallScore(pdf, scores.overall);
  renderScoreBars(pdf, scores);
  renderRightColumnContent(pdf, scores, has_policy, has_environmental_initiative);
};

// Main front page generation
const generateFrontPage = async (
  pdf_document: jsPDF,
  company_name = 'Energy Partners',
  scores: GaugeScores = {
    environmental: 100,
    social: 100,
    governance: 100,
    overall: 100
  },
  has_policy = true,
  has_environmental_initiative = true
): Promise<void> => {
  // Save current font state
  const original_font: Font = pdf_document.getFont();
  const original_font_size: number = pdf_document.getFontSize();
  const original_text_color: string = pdf_document.getTextColor();

  try {
    await renderSvgBackground(pdf_document);

    await addGaugeNeedle(
      pdf_document,
      scores.overall,
      LAYOUT.gauge.center_x,
      LAYOUT.gauge.center_y,
      LAYOUT.gauge.needle_height
    );

    addTextContent(pdf_document, company_name, scores, has_policy, has_environmental_initiative);
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'generateFrontPage'),
      { pdf_document },
      tracking_id
    );
  } finally {
    // Restore original font state
    pdf_document.setFont(original_font.fontName, original_font.fontStyle);
    pdf_document.setFontSize(original_font_size);
    pdf_document.setTextColor(original_text_color);
  }
};

/**
 * Generates the new front page of the readiness gauge PDF download (currently just for the gbcsa conference).
 * @param {jspdf} pdf_document Existing PDF document to append the front page elements to.
 * @param {string} company_name Company name to display as header on front page.
 * @param {GaugeScores} scores Aggregated category (E,S,G) specific scores to show on the front page of the PDF.
 * @param {boolean} has_policy Compliance check for policy KPI sections of readiness gauge, used for conditional paragraph display logic.
 * @param {boolean} has_environmental_initiative Compliance check for environmental KPI sections of readiness gauge, used for conditional paragraph display logic.
 */
export const generateSyntiroGaugeFrontPage = async (
  pdf_document: jspdf,
  company_name?: string,
  scores?: GaugeScores,
  has_policy = true,
  has_environmental_initiative = true
): Promise<void> => {
  try {
    await generateFrontPage(
      pdf_document,
      company_name,
      scores,
      has_policy,
      has_environmental_initiative
    );
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'generateSyntiroGaugeFrontPage'),
      {},
      tracking_id
    );
  }
};
